import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { setCustomTeamRoleAliases } from '../utils/teamRoles';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(localStorage.getItem('token'));

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    // Init axios header on load if token exists
    if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    useEffect(() => {
        if (token) {
            fetchUser();
        } else {
            setLoading(false);
        }
    }, []); // Run once on mount

    const fetchUser = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${BACKEND_URL}/api/auth/me`);
            const incomingUser = res.data || {};
            const normalizedRoles = Array.from(new Set([
                ...(Array.isArray(incomingUser.roles) ? incomingUser.roles : []),
                ...(incomingUser.role ? [incomingUser.role] : [])
            ].filter(Boolean)));
            setUser({
                ...incomingUser,
                roles: normalizedRoles,
                role: incomingUser.role || normalizedRoles[0] || 'member'
            });
            // Refresh custom team-role alias map so deriveTeamMemberRole stays in sync
            // with the admin-configured mappings on the backend
            try {
                const trm = await axios.get(`${BACKEND_URL}/api/settings/team-role-mappings`);
                setCustomTeamRoleAliases(trm.data?.mappings || []);
            } catch (e) {
                // Non-fatal: fallback is the built-in aliases only
                console.warn("Could not load custom team-role mappings", e);
            }
        } catch (error) {
            console.error("Failed to fetch user", error);
            // Don't logout immediately on network error, but on 401 yes
            if (error.response && error.response.status === 401) {
                logout();
            }
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password, club_id = null) => {
        let res = null;
        let lastError = null;

        for (let attempt = 0; attempt < 3; attempt += 1) {
            try {
                res = await axios.post(`${BACKEND_URL}/api/auth/login`, {
                    email, password, ...(club_id ? { club_id } : {})
                });
                break;
            } catch (error) {
                lastError = error;
                const status = error.response?.status;
                const retryable = !error.response || status === 429 || status >= 500;
                if (!retryable || attempt === 2) {
                    throw error;
                }
                await new Promise(resolve => setTimeout(resolve, 1200 * (attempt + 1)));
            }
        }

        if (!res) {
            throw lastError || new Error('Login fehlgeschlagen');
        }

        // Same email in multiple clubs -> caller shows a chooser and re-calls with club_id
        if (res.data?.requires_club_selection) {
            return res.data;
        }

        const newToken = res.data.access_token;

        localStorage.setItem('token', newToken);
        localStorage.setItem('has_logged_in_before', '1');
        setToken(newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

        // Fetch user immediately and wait for it
        await fetchUser();
        return res.data;
    };

    // Authenticate directly with an already-issued token (e.g. the token
    // returned by join-link registration). Avoids a second /login round-trip
    // and makes the join flow independent of the login index lookup.
    const loginWithToken = async (newToken) => {
        if (!newToken) throw new Error('Kein Token erhalten');
        localStorage.setItem('token', newToken);
        localStorage.setItem('has_logged_in_before', '1');
        setToken(newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        await fetchUser();
    };

    const register = async (full_name, email, password, role, extra) => {
        const res = await axios.post(`${BACKEND_URL}/api/auth/register`, { full_name, email, password, role, ...extra });
        return res.data;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
    };

    return (
        <AuthContext.Provider value={{ user, login, loginWithToken, logout, register, loading }}>
            {children}
        </AuthContext.Provider>
    );
};
